import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { generateRandomString } from "utils/helper";
import { createConnectionSmtp, DeleteConnectionSmtp, getCommonTypeByGroupType, SearchConnectionSmtp, testConnectionSmtp } from "../services/configMailApi";
import { message, Modal } from "antd";
import { setCommonTypeByGroupType, setConfigMailList, setIsLoading, setIsLoadingData } from "../slices/configMailSlice";


function useCompanyConfigMail() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoadingData = useSelector((state) => state.configMail.isLoadingData);
  const isLoading = useSelector((state) => state.configMail.isLoading);
  const configMailList = useSelector((state) => state.configMail.configMailList);
  const commonTypeByGroupType = useSelector((state) => state.configMail.commonTypeByGroupType);

  const [showModal, setShowModal] = useState(false)
  const [testConnect, setTestConnect] = useState(true)
  const [type, setType] = useState('')
  const [rowIndex, setRowIndex] = useState([])
  const [pagination, setPagination] = useState({ page: 0, size: 10 });

  const onEdit = (values) => {
    setShowModal(true)
    setType('Modify')
    setRowIndex(values)
  }

  const onAddNew = () => {
    setShowModal(true)
    setType('Add')
  }

  const onCancel = () => {
    setShowModal(false)
  }

  const onDelete = useCallback((id) => {
    Modal.confirm({
      title: t('dialog.confirmation.header'),
      content: (
        <>
          <p>
            {t("dialog.confirmation.delete")}
          </p>
        </>
      ),
      onOk() {
        handleDelete(id);
      },
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      dispatch(setIsLoading(true));
      const response = await DeleteConnectionSmtp({
        id: id,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
      });

      if (response?.status === 200) {
        message.success({ content: t('common.success'), duration: 1 });
        getConfigMailByCompany();
        setShowModal(false)
      } else {
        message.error({ content: t('common.unsuccessful'), duration: 1 });
      }

    } catch (error) {
      message.error({ content: error, duration: 1 });
    } finally {
      dispatch(setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentEmployee, dispatch])

  const onTestConnent = useCallback(async (values) => {

    try {
      dispatch(setIsLoadingData(true));
      setTestConnect(true)
      const response = await testConnectionSmtp({
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee
      });

      if (response?.data === true) {
        message.success({ content: t('common.connentSuccess'), duration: 1 });
        setTestConnect(false)
      }
      else if (response?.data === false) {
        message.error({ content: t('common.connentfalse'), duration: 1 });
        setTestConnect(true)
      }
      else if (response === undefined) {
        message.error({ content: t('common.connentfalse'), duration: 1 });
        setTestConnect(true)
      }

    } catch (error) {
      message.error({ content: error, duration: 1 });
    } finally {
      dispatch(setIsLoadingData(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentEmployee, dispatch])


  const onsubmitModal = useCallback(async (values) => {

    Modal.confirm({
      title: t('dialog.confirmation.header'),
      content: (
        <>
          <p>
            {(type === 'Modify'
              ? t("dialog.confirmation.edit")
              : t("dialog.confirmation.create")
            ).replace("{entity}", t("companyConfigMail.companyConfigMail"))}
          </p>
        </>
      ),
      onOk() {
        handleSave(values);
      },
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = useCallback(async (values) => {
    try {
      dispatch(setIsLoadingData(true));
      const response = await createConnectionSmtp({
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
      });

      if (response.data?.content?.code === '200') {
        message.success({ content: t('common.success'), duration: 1 });
        getConfigMailByCompany();
        setShowModal(false)
      } else if (response.data?.content?.code === '400') {
        message.error({ content: t('common.unsuccessful'), duration: 1 });
      }
    } catch (error) {
      message.error({ content: error, duration: 1 });
    } finally {
      dispatch(setIsLoadingData(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentEmployee, dispatch])

  const onView = (values) => {
    setShowModal(true)
    setType('View')
    setRowIndex(values)
  }

  const handleOnChange = useCallback(
    (tablePagination, tableSorter) => {
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },

        sortBy: modifiedSorter,
      });
      setPagination(modifiedParams);
      getConfigMailByCompany();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSearch = useCallback(async (values) => {
    console.log("🚀 ~ onSearch ~ values:", values)
    try {
      dispatch(setIsLoading(true));
      const response = await SearchConnectionSmtp({
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
        page: pagination.page,
        size: pagination.size,
      });

      dispatch(setConfigMailList(response.data?.content || []));
      setPagination({
        ...pagination,
        total: response.data?.totalItems,
      });
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentEmployee, dispatch])


  const getConfigMailByCompany = useCallback(async () => {
    try {

      dispatch(setConfigMailList([]));

      dispatch(setIsLoading(true));
      const response = await SearchConnectionSmtp({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
        page: pagination.page,
        size: pagination.size,
      });

      dispatch(setConfigMailList(response.data?.content || []));
      setPagination({
        ...pagination,
        total: response.data?.totalItems,
      });
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, dispatch])

  const getCommonType = useCallback(async () => {
    dispatch(setIsLoading(true));
    try {
      const response = await getCommonTypeByGroupType({
        companyId: currentCompany,
        groupType: "MAIL_TYPE_GROUP",
      });
      const options = response?.data?.map(item => ({
        value: item.code,
        label: item.name
      })) || [];
      dispatch(setCommonTypeByGroupType({ options }));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  useEffect(() => {
    getConfigMailByCompany()
    getCommonType()
  }, [getConfigMailByCompany, getCommonType])

  return {
    onSearch,
    onEdit,
    onDelete,
    onView,
    onAddNew,
    setShowModal,
    onChange: handleOnChange,
    onCancel,
    onTestConnent,
    onsubmitModal,
    isLoadingData,
    isLoading,
    pagination,
    configMailList,
    showModal,
    rowIndex,
    type,
    testConnect,
    commonTypeByGroupType
  };
}

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination.page,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

export default useCompanyConfigMail;
